import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { showAlert } from 'actions/app';
import { updateEntityStatus } from 'features/sendouts/services/entityStatus';

import { DRAWER_TYPES } from '../components';
import { SUBMIT_CONFIG } from '../components/DrawerStack/DrawerStack.utils';
import { PROCESS_TYPE } from '../constants/constants';

export const useSendoutsSubmitCoordinator = ({ onSubmitSuccess, closeDrawer, clearStack }) => {
  const dispatch = useDispatch();

  const finalizeSuccessfulSubmit = useCallback(
    async ({ closeOnSuccess = true, meta, normalizedResponse, payload }) => {
      const effects = normalizedResponse.effects || {};

      await onSubmitSuccess({ effects, meta, payload, response: normalizedResponse });
      await meta?.props?.onCustomSubmitSuccess?.();

      if (!closeOnSuccess) {
        return normalizedResponse;
      }

      closeDrawer(meta.key);

      if (payload?.closeAll) {
        clearStack();
      }

      return normalizedResponse;
    },
    [clearStack, closeDrawer, onSubmitSuccess]
  );

  const setSendoverActiveStatus = useCallback(
    async ({ refreshData, sendoverId, status }) => {
      const meta = {
        type: DRAWER_TYPES.SendoverDetails,
        props: {
          sendoverId,
          onCustomSubmitSuccess: refreshData
        }
      };
      const payload = {
        payload: {
          status_id: status?.id
        }
      };

      const response = await updateEntityStatus({
        id: sendoverId,
        payload: payload.payload,
        processType: PROCESS_TYPE.Sendover.title
      });
      const normalizedResponse = {
        success: true,
        ...response,
        effects: {
          refreshTable: true
        }
      };

      if (normalizedResponse.alert) {
        dispatch(showAlert(normalizedResponse.alert));
      }

      if (!normalizedResponse.success) {
        return normalizedResponse;
      }

      return finalizeSuccessfulSubmit({
        closeOnSuccess: false,
        meta,
        normalizedResponse,
        payload
      });
    },
    [dispatch, finalizeSuccessfulSubmit]
  );

  const onDrawerSaved = useCallback(
    async (meta, payload) => {
      const handlerConfig = SUBMIT_CONFIG[meta.type];
      if (!handlerConfig?.handler) {
        throw new Error(`Missing submit handler for drawer type: ${meta.type}`);
      }

      const response = await handlerConfig.handler({ meta, payload });
      const normalizedResponse = { success: true, ...response };

      if (normalizedResponse.alert) {
        dispatch(showAlert(normalizedResponse.alert));
      }

      if (!normalizedResponse.success) {
        return normalizedResponse;
      }

      return finalizeSuccessfulSubmit({ meta, normalizedResponse, payload });
    },
    [dispatch, finalizeSuccessfulSubmit]
  );

  const onDrawerClose = useCallback(
    key => {
      closeDrawer(key);
    },
    [closeDrawer]
  );

  return { setSendoverActiveStatus, onDrawerSaved, onDrawerClose };
};
